import { useEffect, useMemo, useState } from 'react';
import type { KontrolDurumu } from '@mod/domain';
import { api } from '../lib/api';
import {
  LEJANT_SIRASI,
  OBJE_TANIMLARI,
  geciciKonum,
  iskeleTipi,
  type Konum,
  type ObjeTipi,
} from '../lib/harita';

/**
 * SAHA HARİTASI — tesisin kuş bakışı krokisi üzerinde iskele ve malzeme.
 *
 * ⚠️ YAPISAL İSKELET (12.08.2026). Bugün kroki görseli YOK ve pinler
 *    veritabanında DEĞİL; konumlar üniteye göre kümelenmiş, kararlı bir
 *    yer tutucudan üretiliyor (`geciciKonum`). Amaç yapıyı kurmak: veri
 *    akışı, obje tipleri, lejant ve süzme çalışıyor; kroki ile gerçek
 *    pinler yerine oturduğunda değişecek tek şey konumun kaynağı olacak.
 *
 * ⚠️ PİN BIRAKMA YETKİSİ KEŞFE GİDEN KİŞİYE AİT — ve o akış burada DEĞİL.
 *    İş açılışında harita üzerinde lokasyon seçimi YOKTUR; iş açarken
 *    yalnız ünite seçilir ve o davranış korunuyor (IsAc ekranına
 *    dokunulmadı). Pin, keşif adımında bırakılacak.
 */

interface HaritaIskele {
  id: string;
  isEmriNo: string | null;
  iskeleOnayNo: string | null;
  tanim: string;
  unite: string;
  kontrolDurumu: KontrolDurumu;
  vadeGun: number | null;
  hicKontrolYapilmadi: boolean;
  konum: Konum | null;
}

interface HaritaMalzeme {
  id: string;
  isEmriNo: string | null;
  tanim: string;
  unite: string;
  birakilmaTarihi: string | null;
  bekleyenGun: number | null;
  konum: Konum | null;
}

/** Haritada çizilen tekil obje — iki kaynak tek biçime indirgenir. */
interface Obje {
  id: string;
  tip: ObjeTipi;
  baslik: string;
  altBaslik: string;
  unite: string;
  konum: Konum;
  /** Malzemede iş kapalıdır; yine de detayına gidilebilir. */
  jobId: string;
}

/** Kroki oranı — 16:10, tesis krokileri için makul bir kuş bakışı çerçeve. */
const GENISLIK = 1000;
const YUKSEKLIK = 625;

export function Harita({ onIsSec }: { onIsSec: (id: string) => void }) {
  const [d, setD] = useState<{ iskeleler: HaritaIskele[]; malzemeler: HaritaMalzeme[] } | null>(
    null,
  );
  const [hata, setHata] = useState<string | null>(null);
  /** Boş küme = süzme yok. Lejanttan açılıp kapanır. */
  const [suzgec, setSuzgec] = useState<Set<ObjeTipi>>(new Set());
  const [secili, setSecili] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ iskeleler: HaritaIskele[]; malzemeler: HaritaMalzeme[] }>('/harita')
      .then(setD)
      .catch((e: Error) => setHata(e.message));
  }, []);

  const objeler = useMemo<Obje[]>(() => {
    if (!d) return [];
    const iskeleler: Obje[] = d.iskeleler.map((j) => ({
      id: `i-${j.id}`,
      jobId: j.id,
      tip: iskeleTipi(j.kontrolDurumu),
      baslik: j.isEmriNo ?? 'iş emrisiz',
      altBaslik: j.hicKontrolYapilmadi
        ? `${j.vadeGun ?? '—'} gündür kontrol edilmedi (hiç yapılmadı)`
        : `Son kontrol ${j.vadeGun ?? '—'} gün önce`,
      unite: j.unite,
      konum: j.konum ?? geciciKonum(j.id, j.unite),
    }));
    const malzemeler: Obje[] = d.malzemeler.map((m) => ({
      id: `m-${m.id}`,
      jobId: m.id,
      tip: 'malzeme',
      baslik: m.isEmriNo ?? 'iş emrisiz',
      altBaslik:
        m.bekleyenGun != null
          ? `${m.bekleyenGun} gündür sahada bekliyor`
          : 'Söküm sonrası bölgede bırakıldı',
      unite: m.unite,
      konum: m.konum ?? geciciKonum(m.id, m.unite),
    }));
    return [...iskeleler, ...malzemeler];
  }, [d]);

  const sayilar = useMemo(() => {
    const s = {} as Record<ObjeTipi, number>;
    for (const t of LEJANT_SIRASI) s[t] = 0;
    for (const o of objeler) s[o.tip]++;
    return s;
  }, [objeler]);

  const gorunen = useMemo(
    () => (suzgec.size === 0 ? objeler : objeler.filter((o) => suzgec.has(o.tip))),
    [objeler, suzgec],
  );

  const secilenObje = gorunen.find((o) => o.id === secili) ?? null;

  const degistir = (t: ObjeTipi) =>
    setSuzgec((s) => {
      const y = new Set(s);
      if (y.has(t)) y.delete(t);
      else y.add(t);
      return y;
    });

  if (hata) {
    return <p className="m-6 rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>;
  }

  return (
    <div className="mx-auto max-w-6xl p-4 lg:p-6">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-navy">Saha Haritası</h1>
        <p className="text-sm text-wait">
          Tesisin kuş bakışı krokisi üzerinde kurulu iskeleler ve sahada bırakılmış malzeme.
        </p>
      </div>

      {/*
        ⚠️ GEÇİCİ DURUM AÇIKÇA YAZILIYOR.
        Konumlar gerçek değil; kullanıcı haritaya bakıp "iskele burada" diye
        karar verirse yanlış yere gider. Yer tutucu olduğunu ekranın kendisi
        söylemeli — bunu yalnız koda yazmak, ekranı açan kişiye ulaşmaz.
      */}
      <div className="mb-4 rounded-(--radius-mod) border border-warn/30 bg-warn/5 px-4 py-2.5 text-xs text-warn">
        <strong>Yapı kurulumu:</strong> kroki görseli henüz yüklenmedi ve pinler gerçek konumlar
        değil — objeler ünitelerine göre kümelenmiş yer tutucu noktalarda duruyor. Gerçek konum,
        keşfe giden kişinin kroki üzerinde bırakacağı pinden gelecek.
      </div>

      {!d && <p className="py-10 text-center text-sm text-wait">Yükleniyor…</p>}

      {d && (
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="min-w-0 flex-1">
            <HaritaTuvali
              objeler={gorunen}
              secili={secili}
              onSec={(id) => setSecili((v) => (v === id ? null : id))}
            />
          </div>

          <div className="lg:w-64 lg:shrink-0">
            <Lejant sayilar={sayilar} suzgec={suzgec} onDegistir={degistir} onTemizle={() => setSuzgec(new Set())} />

            {secilenObje && (
              <div className="mt-3 rounded-(--radius-mod) border border-line bg-surface p-3 shadow-(--shadow-mod)">
                <div className="flex items-start gap-2">
                  <span
                    className="mt-1 inline-block h-3 w-3 shrink-0"
                    style={{
                      background: OBJE_TANIMLARI[secilenObje.tip].renk,
                      borderRadius:
                        OBJE_TANIMLARI[secilenObje.tip].bicim === 'daire' ? '9999px' : '2px',
                    }}
                  />
                  <div className="min-w-0">
                    <p className="num text-sm font-semibold text-navy">{secilenObje.baslik}</p>
                    <p className="text-xs text-ink-2">{secilenObje.unite}</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-wait">{secilenObje.altBaslik}</p>
                <button
                  onClick={() => onIsSec(secilenObje.jobId)}
                  className="mt-3 w-full rounded-(--radius-mod) bg-navy px-3 py-2 text-xs font-medium text-white"
                >
                  İş detayına git →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  TUVAL                                                              */
/* ================================================================== */

function HaritaTuvali({
  objeler,
  secili,
  onSec,
}: {
  objeler: Obje[];
  secili: string | null;
  onSec: (id: string) => void;
}) {
  const [uzerinde, setUzerinde] = useState<string | null>(null);
  const vurgulu = uzerinde ?? secili;

  return (
    <div className="overflow-hidden rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod)">
      <svg
        viewBox={`0 0 ${GENISLIK} ${YUKSEKLIK}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Saha haritası"
        className="block w-full"
      >
        <defs>
          {/* Kroki gelene kadar zemin: ölçek hissi veren nötr ızgara. */}
          <pattern id="h-izgara" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M50 0 H0 V50" fill="none" stroke="var(--color-line)" strokeWidth="1" />
          </pattern>
          <radialGradient id="h-parlama" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
            <stop offset="70%" stopColor="currentColor" stopOpacity="0.06" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/*
          KROKİ KATMANI — görsel geldiğinde buraya <image> girecek.
          Statik görsel; interaktif harita servisi değil.
        */}
        <rect width={GENISLIK} height={YUKSEKLIK} fill="var(--color-ground)" />
        <rect width={GENISLIK} height={YUKSEKLIK} fill="url(#h-izgara)" />
        <text
          x={GENISLIK / 2}
          y={YUKSEKLIK / 2}
          textAnchor="middle"
          className="fill-wait"
          fontSize="15"
        >
          Tesis krokisi buraya yüklenecek
        </text>

        <g>
          {objeler.map((o) => {
            const t = OBJE_TANIMLARI[o.tip];
            const cx = (o.konum.x / 100) * GENISLIK;
            const cy = (o.konum.y / 100) * YUKSEKLIK;
            const aktif = vurgulu === o.id;
            const r = aktif ? 9 : 7;

            return (
              <g
                key={o.id}
                onClick={() => onSec(o.id)}
                onMouseEnter={() => setUzerinde(o.id)}
                onMouseLeave={() => setUzerinde(null)}
                style={{ cursor: 'pointer' }}
                role="button"
                aria-label={`${t.etiket} — ${o.baslik}`}
              >
                {/* Tıklama alanı çizimden büyük: eldivenli parmak ve fare için. */}
                <circle cx={cx} cy={cy} r="18" fill="transparent" />
                {aktif && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r="18"
                    fill="url(#h-parlama)"
                    color={t.renk}
                    pointerEvents="none"
                  />
                )}

                {/*
                  ⚠️ BİÇİM TİPİ, RENK DURUMU ANLATIR.
                  Malzeme KARE çizilir; renk ayrımı tek başına bırakılsaydı
                  dördüncü bir renk "bu da bir iskele durumu" gibi okunurdu.
                */}
                {t.bicim === 'daire' ? (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill={t.renk}
                    stroke="#fff"
                    strokeWidth="2"
                    pointerEvents="none"
                  />
                ) : (
                  <rect
                    x={cx - r}
                    y={cy - r}
                    width={r * 2}
                    height={r * 2}
                    rx="2"
                    fill={t.renk}
                    stroke="#fff"
                    strokeWidth="2"
                    pointerEvents="none"
                  />
                )}

                {aktif && (
                  <text
                    x={cx}
                    y={cy - 16}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="600"
                    className="fill-ink"
                    pointerEvents="none"
                  >
                    {o.baslik}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

/* ================================================================== */
/*  LEJANT — aynı zamanda süzgeç                                       */
/* ================================================================== */

function Lejant({
  sayilar,
  suzgec,
  onDegistir,
  onTemizle,
}: {
  sayilar: Record<ObjeTipi, number>;
  suzgec: Set<ObjeTipi>;
  onDegistir: (t: ObjeTipi) => void;
  onTemizle: () => void;
}) {
  return (
    <div className="rounded-(--radius-mod) border border-line bg-surface p-3 shadow-(--shadow-mod)">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold tracking-wide text-wait uppercase">Lejant</span>
        {suzgec.size > 0 && (
          <button onClick={onTemizle} className="text-xs text-navy hover:underline">
            Temizle
          </button>
        )}
      </div>

      <div className="flex flex-col gap-0.5">
        {LEJANT_SIRASI.map((tip) => {
          const t = OBJE_TANIMLARI[tip];
          const acik = suzgec.size === 0 || suzgec.has(tip);
          return (
            <button
              key={tip}
              onClick={() => onDegistir(tip)}
              aria-pressed={suzgec.has(tip)}
              title={t.aciklama}
              className={`flex items-center gap-2 rounded-(--radius-mod) px-2 py-2 text-left hover:bg-ground ${
                acik ? '' : 'opacity-40'
              }`}
            >
              <span
                className="h-3 w-3 shrink-0 border-2 border-white"
                style={{
                  background: t.renk,
                  borderRadius: t.bicim === 'daire' ? '9999px' : '2px',
                  boxShadow: '0 0 0 1px rgba(0,0,0,.12)',
                }}
              />
              <span className="min-w-0 flex-1 text-xs text-ink-2">{t.etiket}</span>
              <span className="num text-xs font-semibold text-wait">{sayilar[tip]}</span>
            </button>
          );
        })}
      </div>

      <p className="mt-2 border-t border-line/70 pt-2 text-[11px] leading-relaxed text-wait">
        Malzeme <strong>kare</strong>, iskele <strong>daire</strong> çizilir. Sahada bırakılmış
        malzeme ayrı bir obje tipidir — kurulu iskele listesinde görünmez, yalnız haritada.
      </p>
    </div>
  );
}
